import {getOperationType} from '@sail/data/dist/internal/shared/utils/gql';
import type {ObservabilityFns} from '@sail/data';
import type {Operation} from '@apollo/client';
import internalPbeUnwrapWithError from '../../../../graphql/pbe/internalPbeUnwrapWithError';
import type {PathBuilderParams} from './types';
// Removal of PBE from arguments requires 2 parts to work together:
// - removal of PBE from variables when they arrive at RestLink
// - removal of PBE from arguments to the top level field.
// This function handles the second case.
//
// Example:
// A mutation on `v1SetupIntents` top level field accepts an argument with PBE
// property `mandate_data`. The argument is built fully inside GraphQL document
// In such case there's no pbe_value to strip from variables that the mutation
// receives (in fact it doesn't receive any variables). The transformation needs
// to be done once RestLink calculates the full param that it derives from the
// gql dcoument.
//
// gql`
//   mutation CreateOpalSetupIntentMutation(
//     $v1Context: V1ContextInput!
//   ) {
//     v1SetupIntents(
//       input: {
//         mandate_data: {pbe_value: {customer_acceptance: {type: "online"}}}
//       }
//       v1Context: $v1Context
//     ) {
//       id
//     }
//   }
// `;
export type TopLevelFieldToArgs = {
  queries: Record<string, Record<string, string>>;
  mutations: Record<string, Record<string, string>>;
};

export default function removePbeFromParams(
  operation: Operation,
  params: PathBuilderParams,
  pbeMap: Record<string, ReadonlyArray<string>>,
  topLevelFieldToArgs: TopLevelFieldToArgs,
  observabilityFns: ObservabilityFns | undefined,
) {
  const operationName = operation.operationName;

  const topLevelField = params.fieldName;

  if (!topLevelField) {
    observabilityFns?.errors?.warning(
      'Runtime error when removing PBE from params',
      {
        project: 'sail_core',
        extras: {
          operationName,
          topLevelFieldOrAlias: params['@rest']?.queryName,
        },
      },
    );

    return params;
  }
  const isQuery = getOperationType(operation.query) === 'query';
  const topLevelFiedArgTypes =
    topLevelFieldToArgs[isQuery ? 'queries' : 'mutations'][topLevelField] || {};

  if (!params.args) {
    return params;
  }

  const revisedArgs = internalPbeUnwrapWithError(
    params.args,
    topLevelFiedArgTypes, // map between argument names and their types
    pbeMap, // fields that need to be transformed in each type
  );

  // IMPORTANT: This function must mutate the original params object rather than
  // returning a new object. Here's why:
  //
  // 1. Apollo-link-rest calls pathBuilder(allParams) and bodyBuilder(allParams)
  //    with the SAME object reference. This shared object is how data flows
  //    between these two separate function calls.
  //
  // 2. After removePbeFromParams runs in pathBuilder, buildRestPath makes
  //    critical mutations like: params.args.input = parseQueryParams(queryString)
  //    These mutations must be visible when bodyBuilder is called later.
  //
  // 3. If removePbeFromParams returned new object instead of mutating the
  //    original params object, the code that converts GET to POST requests
  //    would not work anymore, as bodyBuilder would not see the query params
  //    that pathBuilder added to params.args.input.
  //
  // 4. This is also the reason why removePbeFromParams has to be called only
  //    once in pathBuilder and not in both pathBuilder and bodyBuilder.

  params.args = revisedArgs;
}
